import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { Dropdown } from '../../components/Dropdown';
import { FloatingField } from '../../components/FloatingField';
import { FormCard } from '../../components/FormCard';
import { Input } from '../../components/Input';
import {
  HeaderRow,
  HeaderTitle,
  SignInButtonWrapper,
  SplitRow,
  Stack,
  SubheadingLabel,
  Wrapper,
} from './BillingDetailsForm.styles';
import type { BillingDetailsFormLabels, BillingDetailsFormProps } from './BillingDetailsForm.types';

const DEFAULT_LABELS: Required<BillingDetailsFormLabels> = {
  sectionTitle: 'Personal details',
  signInButton: 'Sign in',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  emailLabel: 'Email',
  marketingOptInLabel: 'Get exclusive offers and updates, straight to your inbox.',
  deliveryAddressHeading: 'Delivery address',
  addressLabel: 'Address',
  postalCodeLabel: 'Postcode',
  countryLabel: 'Country',
  countryPlaceholder: 'Select',
  phoneLabel: 'Phone',
  phoneSearchPlaceholder: 'Search country',
};

export function BillingDetailsForm({
  values,
  onChange,
  countryOptions,
  phoneCountryCodes: _phoneCountryCodes,
  onSignInClick,
  showHeader = true,
  showDeliveryAddress = false,
  errors,
  labels,
}: BillingDetailsFormProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };

  const firstNamePlaceholder = `${merged.firstNameLabel}*`;
  const lastNamePlaceholder = `${merged.lastNameLabel}*`;
  const emailPlaceholder = `${merged.emailLabel}*`;
  const addressPlaceholder = `${merged.addressLabel}*`;
  const postalCodePlaceholder = `${merged.postalCodeLabel}*`;
  const countryPlaceholder = `${merged.countryLabel}*`;
  const _phonePlaceholder = `${merged.phoneLabel} (optional)`;

  const nameFields = (
    <FormCard>
      <SplitRow>
        <FloatingField
          label={merged.firstNameLabel}
          hasValue={Boolean(values.firstName)}
          error={errors?.firstName}
        >
          <Input
            type="text"
            autoComplete="given-name"
            value={values.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            error={Boolean(errors?.firstName)}
            placeholder={firstNamePlaceholder}
            aria-label={merged.firstNameLabel}
          />
        </FloatingField>

        <FloatingField
          label={merged.lastNameLabel}
          hasValue={Boolean(values.lastName)}
          error={errors?.lastName}
        >
          <Input
            type="text"
            autoComplete="family-name"
            value={values.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            error={Boolean(errors?.lastName)}
            placeholder={lastNamePlaceholder}
            aria-label={merged.lastNameLabel}
          />
        </FloatingField>
      </SplitRow>
    </FormCard>
  );

  return (
    <Wrapper data-testid="billing-details-form">
      {showHeader && (
        <HeaderRow>
          <HeaderTitle>{merged.sectionTitle}</HeaderTitle>
          {onSignInClick && (
            <SignInButtonWrapper>
              <Button type="button" variant="secondary" size="small" onClick={onSignInClick}>
                {merged.signInButton}
              </Button>
            </SignInButtonWrapper>
          )}
        </HeaderRow>
      )}

      <Stack>
        <FormCard>
          <FloatingField
            label={merged.emailLabel}
            hasValue={Boolean(values.email)}
            error={errors?.email}
          >
            <Input
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(e) => onChange('email', e.target.value)}
              error={Boolean(errors?.email)}
              placeholder={emailPlaceholder}
              aria-label={merged.emailLabel}
            />
          </FloatingField>
        </FormCard>

        <Checkbox
          checked={values.marketingOptIn}
          onChange={(checked) => onChange('marketingOptIn', checked)}
          label={merged.marketingOptInLabel}
        />

        {showDeliveryAddress && (
          <>
            <SubheadingLabel>{merged.deliveryAddressHeading}</SubheadingLabel>

            {nameFields}

            <FormCard>
              <FloatingField
                label={merged.addressLabel}
                hasValue={Boolean(values.address)}
                error={errors?.address}
              >
                <Input
                  type="text"
                  autoComplete="street-address"
                  value={values.address}
                  onChange={(e) => onChange('address', e.target.value)}
                  error={Boolean(errors?.address)}
                  placeholder={addressPlaceholder}
                  aria-label={merged.addressLabel}
                />
              </FloatingField>

              <SplitRow>
                <FloatingField
                  label={merged.postalCodeLabel}
                  hasValue={Boolean(values.postalCode)}
                  error={errors?.postalCode}
                >
                  <Input
                    type="text"
                    autoComplete="postal-code"
                    value={values.postalCode}
                    onChange={(e) => onChange('postalCode', e.target.value)}
                    error={Boolean(errors?.postalCode)}
                    placeholder={postalCodePlaceholder}
                    aria-label={merged.postalCodeLabel}
                  />
                </FloatingField>

                <FloatingField
                  label={merged.countryLabel}
                  hasValue={Boolean(values.country)}
                  error={errors?.country}
                >
                  <Dropdown
                    options={countryOptions}
                    value={values.country || undefined}
                    placeholder={countryPlaceholder}
                    onChange={(v) => onChange('country', v)}
                    aria-label={merged.countryLabel}
                    withSearch
                  />
                </FloatingField>
              </SplitRow>
            </FormCard>
          </>
        )}

        {/* <PhoneFieldWrapper>
          <PhoneInput
            value={values.phone}
            onChange={(v) => onChange('phone', v)}
            countryCodes={phoneCountryCodes}
            error={Boolean(errors?.phone)}
            aria-label={merged.phoneLabel}
            placeholder={phonePlaceholder}
            searchPlaceholder={merged.phoneSearchPlaceholder}
          />
          {errors?.phone && <FieldError>{errors.phone}</FieldError>}
        </PhoneFieldWrapper> */}
      </Stack>
    </Wrapper>
  );
}
